/* eslint-disable @typescript-eslint/naming-convention */
import type { NormalizedCacheObject } from "@apollo/client";
import { dom, config } from "@fortawesome/fontawesome-svg-core";
import { Links, Meta, Scripts, ScrollRestoration } from "@remix-run/react";
import { type ReactNode } from "react";
import { ExternalScripts } from "remix-utils/external-scripts";

import { useServerStyleContext } from "@remix/context/ServerStyleContext";
import { encodeIntoMetaTag } from "@remix/helpers/encodeIntoMetaTag/encodeIntoMetaTag";
import { DialogPolyfillScript } from "@remix/polyfills/DialogPolyfillScript/DialogPolyfillScript";
import type { loader } from "@remix/root";
import { isProduction } from "@shared/utils/fullscriptEnv/fullscriptEnv";

import { ClientEnvVars } from "../ClientEnvVars/ClientEnvVars";
import { ServiceWorker } from "../ServiceWorker/ServiceWorker";

import { AdaJS } from "./AdaJS/AdaJS";

// Don't add FontAwesome CSS during hydration
// It's already extracted and served with the HTML
config.autoAddCss = false;

interface Props {
  initialApolloState?: NormalizedCacheObject;
  initialProps?: any;
  deviceType?: string;
  ENV?: ReturnType<typeof loader>["ENV"];
  children: ReactNode;
}

const Document = ({ children, ENV, deviceType, initialProps, initialApolloState }: Props) => {
  const serverStyleData = useServerStyleContext();
  const googleMapsApiPubKey = ENV?.GOOGLE_MAPS_API_PUB_KEY;

  const renderStyles = () => {
    return serverStyleData?.map(({ key, ids, css }) => (
      <style
        key={key}
        data-emotion={`${key} ${ids.join(" ")}`}
        dangerouslySetInnerHTML={{ __html: css }}
      />
    ));
  };

  const renderClientEnvVars = () => {
    if (!ENV) return null;

    // Anything passed in here will be exposed on the client
    // Don't pass anything that is a secret!
    return <ClientEnvVars env={ENV} />;
  };

  const renderNoIndexMeta = () => {
    if (isProduction()) return;

    return <meta name="robots" content="noindex, nofollow, nosnippet, noimageindex" />;
  };

  const renderInitialProps = () => {
    if (!initialProps || Object.keys(initialProps).length === 0) return null;

    return encodeIntoMetaTag("initial-props", JSON.stringify(initialProps));
  };

  const renderApolloState = () => {
    if (!initialApolloState || Object.keys(initialApolloState).length === 0) return null;

    return encodeIntoMetaTag("apollo-state", JSON.stringify(initialApolloState));
  };

  const renderDeviceType = () => {
    if (typeof window !== "undefined" || !deviceType) return null;

    return encodeIntoMetaTag("device-type", deviceType);
  };

  return (
    <html lang="en-US">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        {renderNoIndexMeta()}
        {renderDeviceType()}
        {renderApolloState()}
        {renderInitialProps()}
        <Meta />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" />
        <style dangerouslySetInnerHTML={{ __html: dom.css() }} />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Besley:wght@500&family=Mulish:wght@400;500;600;700;800&display=swap"
        />
        <DialogPolyfillScript />
        <script
          src={`https://maps.googleapis.com/maps/api/js?key=${googleMapsApiPubKey}&loading=async&libraries=places&callback=Function.prototype`}
        />
        <Links />
        {renderStyles()}
        {renderClientEnvVars()}
        <ServiceWorker />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
        <ExternalScripts />
        <AdaJS />
      </body>
    </html>
  );
};

export { Document };
